/**
 * CurtainAdminModal.jsx - Admin panel for managing curtain images and quotes
 * Shows ALL content: defaults + custom, with edit controls for custom only
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Image, MessageSquare, Upload, Loader2, Lock, Info } from 'lucide-react';
import { FRUGAL_QUOTES } from '../data/frugalQuotes';
import { FeedbackToast, ImageItem, QuoteItem } from './CurtainAdminParts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default image (predeterminada)
const DEFAULT_IMAGES = [
  { url: 'https://customer-assets.emergentagent.com/job_52b9527d-3c1a-4572-bf81-2e6452d816a6/artifacts/44nejtgr_24f6c653-3df2-43e4-8008-96400c7635a1-2.png', name: 'Ilustración Frugal (predeterminada)', isDefault: true }
];

const CurtainAdminModal = ({ isOpen, onClose, token }) => {
  const [customImages, setCustomImages] = useState([]);
  const [customQuotes, setCustomQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('images');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [newQuoteText, setNewQuoteText] = useState('');
  const [newQuoteAuthor, setNewQuoteAuthor] = useState('');
  const [showDefaults, setShowDefaults] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) { fetchConfig(); setFeedback({ type: '', message: '' }); }
  }, [isOpen]);

  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => setFeedback({ type: '', message: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/v2/frugal/curtain/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCustomImages(data.images || []);
      setCustomQuotes(data.quotes || []);
    } catch (err) {
      setFeedback({ type: 'error', message: 'Error al cargar configuración' });
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setFeedback({ type: 'error', message: 'Solo imágenes (JPG, PNG, GIF, WebP)' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFeedback({ type: 'error', message: 'Máximo 5MB' });
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/v2/frugal/curtain/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Error al subir');
      const data = await res.json();
      await fetch(`${API_URL}/api/v2/frugal/curtain/images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: data.url, name: file.name, active: true })
      });
      setFeedback({ type: 'success', message: 'Imagen subida' });
      fetchConfig();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Error al subir imagen' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = async (url) => {
    try {
      await fetch(`${API_URL}/api/v2/frugal/curtain/images?url=${encodeURIComponent(url)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      setFeedback({ type: 'success', message: 'Imagen eliminada' });
      fetchConfig();
    } catch { setFeedback({ type: 'error', message: 'Error al eliminar' }); }
  };

  const addQuote = async () => {
    if (!newQuoteText.trim()) return;
    try {
      setSaving(true);
      await fetch(`${API_URL}/api/v2/frugal/curtain/quotes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newQuoteText, author: newQuoteAuthor || 'Anónimo', active: true })
      });
      setNewQuoteText(''); setNewQuoteAuthor('');
      setFeedback({ type: 'success', message: 'Frase agregada' });
      fetchConfig();
    } catch { setFeedback({ type: 'error', message: 'Error al agregar' }); }
    finally { setSaving(false); }
  };

  const removeQuote = async (text) => {
    try {
      await fetch(`${API_URL}/api/v2/frugal/curtain/quotes?text=${encodeURIComponent(text)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      setFeedback({ type: 'success', message: 'Frase eliminada' });
      fetchConfig();
    } catch { setFeedback({ type: 'error', message: 'Error al eliminar' }); }
  };

  if (!isOpen) return null;

  const totalImages = DEFAULT_IMAGES.length + customImages.length;
  const totalQuotes = FRUGAL_QUOTES.length + customQuotes.length;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/80 overflow-y-auto">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md mx-4 my-4 flex flex-col" style={{ maxHeight: 'calc(100vh - 32px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">Configurar Cortina</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-full">
            <X size={20} />
          </button>
        </div>

        <FeedbackToast feedback={feedback} />

        {/* Info banner */}
        <div className="mx-4 mt-3 px-3 py-2 bg-slate-700/50 rounded-lg flex items-start gap-2 text-xs text-slate-400">
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <span>Las predeterminadas (<Lock size={10} className="inline" />) no se pueden borrar. Tus personalizadas sí.</span>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700 flex-shrink-0 mt-3">
          <button onClick={() => setActiveTab('images')}
            className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm ${activeTab === 'images' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}>
            <Image size={16} /> Imágenes ({totalImages})
          </button>
          <button onClick={() => setActiveTab('quotes')}
            className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm ${activeTab === 'quotes' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}>
            <MessageSquare size={16} /> Frases ({totalQuotes})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {loading ? <div className="text-center text-slate-400 py-8">Cargando...</div> : activeTab === 'images' ? (
            <div className="space-y-4">
              {/* Upload */}
              <div className="bg-slate-700/50 rounded-lg p-3">
                <h3 className="text-sm font-medium text-emerald-400 mb-2">Subir imagen desde tu PC</h3>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="w-full py-3 border-2 border-dashed border-slate-500 hover:border-emerald-500 rounded-lg flex items-center justify-center gap-2 text-slate-300 hover:text-emerald-300">
                  {uploading ? <><Loader2 size={20} className="animate-spin" /> Subiendo...</> : <><Upload size={20} /> Seleccionar imagen</>}
                </button>
                <div className="mt-2 p-2 bg-emerald-900/30 rounded-lg border border-emerald-500/20">
                  <p className="text-emerald-300 text-xs font-medium">Tamano recomendado:</p>
                  <p className="text-emerald-200 text-xs">1000 x 500 px (proporcion 2:1 horizontal)</p>
                  <p className="text-slate-400 text-xs mt-1">Formatos: JPG, PNG, GIF, WebP. Max 5MB</p>
                </div>
              </div>

              {customImages.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-emerald-400 uppercase tracking-wide">Tus imágenes ({customImages.length})</h4>
                  {customImages.map((img, idx) => <ImageItem key={`custom-${idx}`} img={img} onRemove={removeImage} isDefault={false} />)}
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide">Predeterminadas ({DEFAULT_IMAGES.length})</h4>
                {DEFAULT_IMAGES.map((img, idx) => <ImageItem key={`default-${idx}`} img={img} isDefault={true} />)}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
                <h3 className="text-sm font-medium text-emerald-400">Agregar frase personalizada</h3>
                <textarea placeholder="Texto de la frase" value={newQuoteText} rows={2}
                  onChange={(e) => setNewQuoteText(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm resize-none focus:border-emerald-500 focus:outline-none" />
                <input type="text" placeholder="Autor" value={newQuoteAuthor}
                  onChange={(e) => setNewQuoteAuthor(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none" />
                <button onClick={addQuote} disabled={saving || !newQuoteText.trim()}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white rounded-lg flex items-center justify-center gap-2 text-sm">
                  <Plus size={16} /> {saving ? 'Agregando...' : 'Agregar Frase'}
                </button>
              </div>

              {customQuotes.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-emerald-400 uppercase tracking-wide">Tus frases ({customQuotes.length})</h4>
                  {customQuotes.map((q, idx) => <QuoteItem key={`custom-${idx}`} q={q} onRemove={removeQuote} isDefault={false} />)}
                </div>
              )}

              <div className="space-y-2">
                <button onClick={() => setShowDefaults(!showDefaults)} 
                  className="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wide hover:text-slate-300">
                  <span>Predeterminadas ({FRUGAL_QUOTES.length})</span>
                  <span className="text-slate-600">{showDefaults ? 'Ocultar' : 'Ver todas'}</span>
                </button>
                {showDefaults && (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {FRUGAL_QUOTES.map((q, idx) => <QuoteItem key={`default-${idx}`} q={q} isDefault={true} />)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 flex-shrink-0">
          <button onClick={onClose} className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CurtainAdminModal;
